import { redirect, type LoaderFunctionArgs } from "react-router";
import { dashboardQuery, queryClient } from "../api/queries";
import type { Session } from "../api/types";

/**
 * Who may see the panel is settled before anything is painted.
 *
 * The panel used to paint the sign-in screen first and take it away once the session
 * answered, so every reload flashed it. Now the router waits for the session — and the
 * server already refuses the page to whoever has none — so what appears is either the
 * panel or the sign-in, never one and then the other.
 */

async function readSession(): Promise<Session> {
  const response = await fetch("/auth/session", { cache: "no-store", credentials: "same-origin" });
  if (!response.ok) throw new Response("No se pudo comprobar la sesión", { status: response.status });
  return (await response.json()) as Session;
}

/** Only addresses inside the panel: `?next=https://…` must not turn sign-in into a redirector. */
export function safeNext(value: string | null): string {
  return value && value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/login") ? value : "/";
}

/** The panel: whoever has no session goes to sign in and comes back to the same address. */
export async function panelLoader({ request }: LoaderFunctionArgs) {
  const session = await readSession();
  if (!session.authenticated) {
    const url = new URL(request.url);
    throw redirect(`/login?next=${encodeURIComponent(url.pathname + url.search)}`);
  }
  // Every screen hangs off the dashboard: having it before the first paint means no screen
  // ever flashes its "nothing here yet" while the figures are on their way.
  await queryClient.ensureQueryData(dashboardQuery);
  return session;
}

/** Sign-in: whoever already has a session has nothing to do here. */
export async function loginLoader({ request }: LoaderFunctionArgs) {
  const session = await readSession();
  const next = safeNext(new URL(request.url).searchParams.get("next"));
  if (session.authenticated) throw redirect(next);
  return { session, next };
}
